import { formatDistance } from "date-fns";
import React from "react";
import { Link } from "react-router-dom";
import {
  ReprocessDataTaskFragment,
  ReprocessDataTaskStatus,
} from "../../graphql/generated";
import Timestamp from "../../common/components/timestamp";
import { Status } from "../../common/components/status";

const ReprocessDataTaskTableListItem: React.FC<{
  task?: ReprocessDataTaskFragment;
}> = ({ task }) => {
  if (!task) return null;
  let percentCompleteDisplay = "—";
  let remainingDisplay = "—";
  let approximateEta;
  let aboveTargetSchemaVersion = 0;
  let belowTargetSchemaVersion = 0;
  const targetSchemaVersion = task.targetSchemaVersion;

  for (const item of task.schemaVersionCounts) {
    if (item.schemaVersion == "total") {
      continue;
    } else if (item.schemaVersion == "older") {
      belowTargetSchemaVersion += item.count;
    } else if (item.schemaVersion >= targetSchemaVersion) {
      aboveTargetSchemaVersion += item.count;
    } else {
      belowTargetSchemaVersion += item.count;
    }
  }

  const total = aboveTargetSchemaVersion + belowTargetSchemaVersion;
  const ratioComplete = total > 0 ? aboveTargetSchemaVersion / total : 1;
  percentCompleteDisplay = ratioComplete.toLocaleString(undefined, {
    style: "percent",
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  });
  remainingDisplay = belowTargetSchemaVersion.toLocaleString(undefined);

  const approximateRatePerSecond =
    task.totalRunTimeMilliseconds === 0
      ? undefined
      : task.totalProcessed / (task.totalRunTimeMilliseconds / 1000);

  const remainingSeconds =
    (approximateRatePerSecond === undefined || approximateRatePerSecond === 0)
      ? undefined
      : belowTargetSchemaVersion / approximateRatePerSecond;

  switch (task.status) {
    case ReprocessDataTaskStatus.Finished:
      approximateEta = "Finished";
      break;
    case ReprocessDataTaskStatus.Abandoned:
      approximateEta = "Abandoned";
      break;
    case ReprocessDataTaskStatus.Paused:
    case ReprocessDataTaskStatus.PausedForProblems:
    case ReprocessDataTaskStatus.Pausing:
    case ReprocessDataTaskStatus.WaitingOnCache:
    case ReprocessDataTaskStatus.Ready:
      if (remainingSeconds !== undefined) {
        const now = new Date();
        const future = new Date(now.valueOf());
        future.setSeconds(future.getSeconds() + remainingSeconds);
        approximateEta = formatDistance(future, now);
      }
      break;
    case ReprocessDataTaskStatus.Running:
      if (remainingSeconds !== undefined) {
        const now = new Date();
        const future = new Date(now.valueOf());
        future.setSeconds(future.getSeconds() + remainingSeconds);
        approximateEta = (
          <Timestamp format="long" popover timestamp={future.toISOString()} />
        );
      }
      break;
  }

  return (
    <Link
      to={`/reprocess_data_tasks/${task?.id}`}
      className="relative border-b border-gray-300 px-2 py-2 grid grid-cols-6 gap-2 shadow-sm items-center hover:bg-zenlist-50 focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-indigo-500"
    >
      <span>{Status.for(task).text}</span>
      <span>{task?.rootResource.mls.shortName}</span>
      <span>{percentCompleteDisplay}</span>
      <span>{approximateEta ?? "—"}</span>
      <span>
        {task &&
          task.problemThreshold !== null &&
          task.problemsSinceLastStart.toLocaleString()}
        {task && task.problemThreshold
          ? " (" + task.problemThreshold.toLocaleString() + ")"
          : " (∞)"}
      </span>
      <span>{remainingDisplay}</span>
    </Link>
  );
};

export default ReprocessDataTaskTableListItem;
